import { PopupPositionConfig } from './popopver.config';

export const POPOVER_TABLE_SELECTOR = '.ag-root-wrapper';

const ARROW_EDGE_INSET = 10;

export interface PopoverBoundary {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface PopoverCellRect {
  left: number;
  width: number;
  top: number;
  bottom: number;
}

export interface PopoverPaneSize {
  width: number;
  height: number;
}

export interface PopoverPlacement {
  offsetX: number;
  preferBelow: boolean;
  arrowShift: number;
}

/**
 * Visible rect of the table that hosts the popover: the ag-grid wrapper
 * horizontally, the nearest scrolling ancestor vertically (a heat-map table is
 * several stacked grids in one scroll container), both narrowed by clipping
 * ancestors and the viewport. Returns null when the host is not inside a grid.
 */
export function getPopoverBoundaryRect(
  host: HTMLElement
): PopoverBoundary | null {
  const grid = host.closest(POPOVER_TABLE_SELECTOR);
  if (!grid) {
    return null;
  }

  const gridRect = grid.getBoundingClientRect();
  let left = gridRect.left;
  let right = gridRect.right;
  let top: number | null = null;
  let bottom: number | null = null;

  for (
    let el = grid.parentElement;
    el && el !== document.documentElement;
    el = el.parentElement
  ) {
    const style = window.getComputedStyle(el);
    const clipsX = style.overflowX !== 'visible';
    const clipsY = style.overflowY !== 'visible';
    if (!clipsX && !clipsY) {
      continue;
    }
    const rect = el.getBoundingClientRect();
    if (clipsX) {
      left = Math.max(left, rect.left);
      right = Math.min(right, rect.right);
    }
    if (clipsY) {
      top = top === null ? rect.top : Math.max(top, rect.top);
      bottom = bottom === null ? rect.bottom : Math.min(bottom, rect.bottom);
    }
  }

  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;

  return {
    left: Math.max(left, 0),
    right: Math.min(right, viewportWidth),
    top: Math.max(top ?? gridRect.top, 0),
    bottom: Math.min(bottom ?? gridRect.bottom, viewportHeight),
  };
}

export function computePopoverPlacement(
  cell: PopoverCellRect,
  pane: PopoverPaneSize,
  boundary: PopoverBoundary
): PopoverPlacement {
  const [above, below] = PopupPositionConfig;
  const left0 = cell.left + cell.width / 2 - pane.width / 2;
  const right0 = left0 + pane.width;

  let offsetX = 0;
  if (pane.width > boundary.right - boundary.left) {
    offsetX = boundary.left - left0;
  } else if (right0 > boundary.right) {
    offsetX = boundary.right - right0;
  } else if (left0 < boundary.left) {
    offsetX = boundary.left - left0;
  }

  const fitsAbove =
    cell.top + (above.offsetY ?? 0) - pane.height >= boundary.top;
  const fitsBelow =
    cell.bottom + (below.offsetY ?? 0) + pane.height <= boundary.bottom;

  const arrowLeft = Math.min(
    Math.max(pane.width / 2 - offsetX, ARROW_EDGE_INSET),
    pane.width - ARROW_EDGE_INSET
  );

  return {
    offsetX,
    preferBelow: !fitsAbove && fitsBelow,
    arrowShift: arrowLeft - pane.width / 2,
  };
}
